/**
 * Whether this account is a HubSpot-path sign-up still waiting on its trial.
 *
 * The server derives it per request, so the moment the trial starts it is
 * false. Read by the gate inside the app shell and by the public header.
 * A failed read is "not pending": the gate is a curtain in front of the
 * server's own refusals, and a curtain that jams shut on a network blip
 * would lock a paying customer out of their dashboard.
 */
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { apiService } from '@/services/api'
import type { OnboardingStanding } from '@/types/hubspotOnboarding'

export const ONBOARDING_STANDING_QUERY_KEY = ['onboarding', 'standing'] as const

const NOT_PENDING: OnboardingStanding = { pending: false, scanId: null, domain: null }

/** The report the pending account is sent back to. */
export function buildStandingReturnPath(standing: OnboardingStanding): string {
  const params = new URLSearchParams()
  if (standing.scanId) params.set('scanId', standing.scanId)
  if (standing.domain) params.set('domain', standing.domain)
  const query = params.toString()
  return query ? `/welcome/results?${query}` : '/welcome/results'
}

export function useOnboardingStanding(): { standing: OnboardingStanding; isLoading: boolean } {
  const { isLoaded, isSignedIn } = useAuth()
  const enabled = Boolean(isLoaded && isSignedIn)
  const { data, isLoading, isError } = useQuery({
    queryKey: ONBOARDING_STANDING_QUERY_KEY,
    queryFn: async () => (await apiService.getOnboardingStanding()).data ?? NOT_PENDING,
    enabled,
    /* Fresh on every mount of the shell or the header: the answer changes
       the moment a trial starts, and a stale "pending" would turn a new
       customer back from the dashboard they just paid for. */
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  })
  if (!enabled || isError) return { standing: NOT_PENDING, isLoading: false }
  return { standing: data ?? NOT_PENDING, isLoading }
}
